import React, { useCallback, useEffect, useRef, useState } from "react";
import classes from "./Login.module.scss";
import QRCode from "react-qr-code";
import CircularIndeterminate from "components/utils/spinner/Spinner";
import { LinearProgress } from "@mui/material";
// Services
import { loginQREvent, loginQRGet, loginQRMerchant, loginServicioGet } from "services/services-mia/login";
import { usuarioMinimosGet } from "services/services-mia";
import { merchantLogoGet } from "services/services-mia/merchant";

import { checkAuth } from "services/checkAuth";
// Recoil
import {
  userCredentials,
  snackbarData,
  userInfo,
  rioNegroServices,
} from "recoilState/GlobalState";
import { useSetRecoilState } from "recoil";
import { LogoRioNegro, RNQR } from "assets/icons";
import { Routes } from "consts/Routes-App";
import { ESTADO_UBICACION, useUbicacion } from "components/utils/useUbicacion";
import { useLocation, useNavigate } from "react-router-dom";

// Este backoffice es exclusivamente el de Sistema de Salud. Si el QR devuelve varios
// merchants habilitados se entra siempre con este, sin preguntar; si no lo tiene, no entra.
// Se matchea por merchantCod y no por merchantId porque el id cambia entre ambientes.
const MERCHANT_COD = "SSRN";

/**
 * Lo que se muestra en lugar del QR cuando no hay ubicacion.
 *
 * El acceso por QR la manda al back para validar la sesion, asi que sin ella no hay
 * codigo que generar. Antes se veia el texto "escanea el QR" sin ningun QR debajo.
 *
 * El boton solo aparece donde REINTENTAR SIRVE. Con el permiso bloqueado, volver a
 * llamar a getCurrentPosition no muestra el cartel de nuevo: hay que desbloquearlo desde
 * el navegador, y por eso ahi se explica como. Cuando el usuario lo hace, el listener de
 * `onchange` levanta el cambio y el QR aparece solo, sin recargar.
 */
function AvisoUbicacion({ estado, onReintentar }) {
  const MENSAJES = {
    [ESTADO_UBICACION.PENDIENTE]: {
      titulo: "Necesitamos tu ubicación",
      texto: "El ingreso con QR la usa para validar la sesión.",
      boton: "Permitir ubicación",
    },
    [ESTADO_UBICACION.BLOQUEADA]: {
      titulo: "La ubicación está bloqueada",
      texto:
        "Habilitá el permiso en las configuraciones y vas a poder ver el código QR",
    },
    [ESTADO_UBICACION.NO_SEGURO]: {
      titulo: "La página no es segura",
      texto:
        "El ingreso con QR necesita tu ubicación, y el navegador solo la comparte por HTTPS. Entrá con https:// o avisá a soporte.",
    },
    [ESTADO_UBICACION.ERROR]: {
      titulo: "No pudimos obtener tu ubicación",
      texto: "Puede ser un problema momentáneo del dispositivo.",
      boton: "Reintentar",
    },
  };

  const { titulo, texto, boton } =
    MENSAJES[estado] ?? MENSAJES[ESTADO_UBICACION.ERROR];

  return (
    <>
      <h4>{titulo}</h4>
      <p>{texto}</p>
      {boton && (
        <button className={classes.button} onClick={onReintentar}>
          {boton}
        </button>
      )}
    </>
  );
}

export default function Login() {
  const setCredentials = useSetRecoilState(userCredentials);
  const setSnackbarInfo = useSetRecoilState(snackbarData);
  const setInfo = useSetRecoilState(userInfo);
  const setServices = useSetRecoilState(rioNegroServices);
  const navigate = useNavigate();
  const location = useLocation();

  // `coord` sale del mismo estado que llena App al arrancar; de aca solo se usa el
  // motivo y el reintento, para poder explicar por que no aparece el QR.
  const { coords: coord, estado: estadoUbicacion, reintentar } = useUbicacion();

  const [UUID, setUUID] = useState();
  const [loading, setLoading] = useState(false);
  const [expirado, setExpirado] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressTimer, setProgressTimer] = useState();
  const intentos = useRef(0);
  const from = useRef(location.state?.from?.pathname + location.state?.from?.search || Routes.dashboard.route);

  useEffect(() => {
    if (checkAuth()) {
      navigate(from.current, { replace: true });
      return;
    }

    return () => {
      clearInterval(progressTimer);
    };
  }, []);

  useEffect(() => {
    if (coord?.lat && coord?.lng) getQR();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coord]);

  useEffect(() => {
    if (UUID) {
      initProgressTimer();
      getQREvent(UUID);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UUID]);

  const getQR = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!coord?.lat || !coord?.lng) return;
    setExpirado(false);
    setLoading(true);
    const { result } = await loginQRGet({
      sesionLatitud: coord.lat.toFixed(6),
      sesionLongitud: coord.lng.toFixed(6),
    });
    const data = result[0]

    setUUID(data.codigoQR);
    setLoading(false);
    return data.codigoQR;
  }, [coord]);

  const initProgressTimer = () => {
    setProgress(0);
    const timer = setInterval(() => {
      if (progress >= 100) {
        clearInterval(timer);
      }
      setProgress((oldProgress) => (
        //140 segundos en 580 saltos hasta completar 100%
        (oldProgress >= 100)
          ? 100
          : oldProgress + (100 / 580)
      ));
    }, ((140 * 1000) / 580));
    setProgressTimer(timer);
  }

  const loginDo = useCallback(async (lbmUUID, merchant) => {
    const { result: credentials, status } = await loginQRMerchant({
      codigoQR: lbmUUID,
      merchantId: merchant.merchantId,
    });
    if (status.code !== 1) {
      setSnackbarInfo({
        open: true,
        message: "Login no autorizado",
        severity: "error",
      });
      setTimeout(() => {
        setSnackbarInfo({
          open: false,
          message: "",
          severity: "error",
        });
      }, 5000);
      return
    }
    localStorage.setItem("hooliToken", credentials[0].hooliToken);
    localStorage.setItem("sessionId", credentials[0].sessionId);
    localStorage.setItem("merchantId", merchant.merchantId);
    localStorage.setItem("merchantNombre", merchant.merchantNombre);
    localStorage.setItem("merchantRZ", merchant.merchantRZ);
    localStorage.setItem("merchantCUIT", merchant.merchantCUIT);
    const newCredentials = {
      ...credentials[0],
      ...merchant,
    };
    setCredentials(newCredentials);

    const [userData, getMerchantLogoResponse, getServicesResponse] = await Promise.all([
      usuarioMinimosGet(newCredentials),
      merchantLogoGet({}, newCredentials),
      loginServicioGet({}, newCredentials)
    ])
    setInfo({
      ...getMerchantLogoResponse.result[0],
      ...userData
    });
    setServices(getServicesResponse.result);

    navigate(from.current, { replace: true });
  }, [navigate, setCredentials, setInfo, setServices, setSnackbarInfo])

  const getQREvent = useCallback(async (lbmUUID) => {
    intentos.current++;
    const { result: merchants, status } = await loginQREvent({
      codigoQR: lbmUUID,
    });
    if (status.code !== 1) {
      if (status.action === 'X') {
        setUUID(null);
        switch (status.errcode) {
          case 30763:
            setExpirado(true);

            setSnackbarInfo({
              open: true,
              message: "Usuario no autorizado",
              severity: "error",
            });
            setTimeout(() => {
              setSnackbarInfo({
                open: false,
                message: "",
                severity: "error",
              });
            }, 5000);
            return;
          default:
            console.warn('loginQREvent:', status)
            break;
        }
      }
      if (intentos.current > 2) {
        setExpirado(true);
        return;
      }
      getQR();
      return;
    }
    const merchant = merchants.find((item) => item.merchantCod === MERCHANT_COD);
    if (!merchant) {
      setSnackbarInfo({
        open: true,
        message: "Usuario no autorizado",
        severity: "error",
      });
      setTimeout(() => {
        setSnackbarInfo({
          open: false,
          message: "",
          severity: "error",
        });
      }, 5000);
      return;
    }
    await loginDo(lbmUUID, merchant);
  }, [getQR, loginDo, setSnackbarInfo]);

  return (
    <main className={classes.root}>
      <div className={classes.logoContainer}>
        <img src={LogoRioNegro} alt="logo-rn" />
      </div>
      <div className={classes.login}>
        <div className={classes.container}>
          <div className={classes.qrContainer}>
            {expirado ? (
              <>
                <h4>Ups!</h4>
                <p>
                  El código caducó, generá un{" "}
                  <span>nuevo QR</span> para poder ingresar.
                </p>
                <img src={RNQR} alt="Celular" />
                <button
                  className={classes.button}
                  onClick={getQR}
                >
                  Generar QR
                </button>
              </>
            ) : estadoUbicacion !== ESTADO_UBICACION.OK &&
              estadoUbicacion !== ESTADO_UBICACION.CARGANDO ? (
              <AvisoUbicacion estado={estadoUbicacion} onReintentar={reintentar} />
            ) : (
              <>
                {!loading && (
                  <>
                    <h4>Hola!</h4>
                    <p>
                      Para poder ingresar, escanea el{" "}
                      <span>QR</span> desde la App de
                      MIA.
                    </p>
                  </>
                )}
                {UUID && !loading && (
                  <>
                    <QRCode value={UUID} />
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      style={{
                        width: "100%",
                        marginTop: "16px",
                      }}
                      color="secondary"
                    />
                  </>
                )}
                {loading && <CircularIndeterminate />}
              </>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
